import { isCloneDeviceSettingsEnabled } from 'src/helpers/settings/settings_config_helpers';
import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
import { capFirstChar } from 'src/helpers/utilities';
import { get_all_fw_versions_gen_2 } from 'src/services/http';
import { type UpdateModeSubTypes, type UpdateModeTypes } from 'src/stores/ui/edit_lists';
import { devicesFilters } from 'src/stores/ui/filters';

export type MenuCommand = {
	title: string;
	icon: string;
	id: number;
	isSelected?: boolean;
	callback?: () => void;
	areFiltersDisabled?: boolean;
	showDropdown?: boolean;
	rightIcon?: string;
	items?: Array<{
		title: string;
		aiSubtype?: UpdateModeSubTypes;
		id: number;
		icon: string;
		[key: string]: any;
	}>;
	[key: string]: any; // Allow additional properties
};

type BuildMenusDeps = {
	format: (key: string, opts?: any) => string;
	userData: any;
	setShowDropdownMenu: (v: boolean) => void;
	setShowAIModelCommandsModal: (v: boolean) => void;
	dispatchSubmit?: (eventName: 'submit-click') => void;
	onChangeClick: (type: UpdateModeTypes, subType?: UpdateModeSubTypes) => void;
	updateStore: (type: UpdateModeTypes) => void;
	newLayout: boolean;
};

const isTestUser = isDevServerUser(['repo', 'nb']) || isT1User();

export function buildDropdownMenus(deps: BuildMenusDeps): {
	initialEditCommands: MenuCommand[];
	bulkCommands: MenuCommand[];
} {
	const {
		format,
		userData,
		setShowDropdownMenu,
		setShowAIModelCommandsModal,
		onChangeClick,
		updateStore,
		newLayout,
	} = deps;

	const onUpdateClick = (type: UpdateModeTypes) => {
		if (type === 'update') {
			get_all_fw_versions_gen_2();
		}
		updateStore(type);
		devicesFilters.update((current) => ({
			...current,
			device_state: current.device_state.map((filter) =>
				filter.id === 'online' ? { ...filter, selected: true } : { ...filter, selected: false },
			),
		}));
	};

	const initialEditCommands: MenuCommand[] = [
		{
			title: format('sub_settings_title_bulk_move_to_another_room_button', {
				default: 'Move to another room',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-tent-arrow-left-right`,
			isSelected: true,
			id: 0,
			callback: () => {
				onChangeClick('bulk_move_devices_to_another_room');
				setShowDropdownMenu(false);
			},
		},
		{
			title: format('delete_devices', { default: 'Delete devices' }),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-trash`,
			isSelected: true,
			id: 1,
			callback: () => {
				onChangeClick('bulk_delete');
				setShowDropdownMenu(false);
			},
		},
	];

	const bulkCommands: MenuCommand[] = [
		!userData?.installer && {
			title: format('sub_settings_title_account_transfer_devices', {
				default: 'Account devices transfer',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-people-arrows`,
			isSelected: true,
			id: 0,
			callback: () => {
				onChangeClick('account_transfer_devices');
				setShowDropdownMenu(false);
			},
		},
		{
			title: format('sub_settings_title_bulk_move_to_another_room_button', {
				default: 'Move to another room',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-tent-arrow-left-right`,
			isSelected: true,
			id: 13,
			callback: () => {
				onChangeClick('bulk_move_devices_to_another_room');
				setShowDropdownMenu(false);
			},
		},
		{
			title: format('update_devices', { default: 'Update devices' }),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-arrows-rotate`,
			isSelected: true,
			id: 1,
			callback: () => {
				onUpdateClick('update');
				setShowDropdownMenu(false);
			},
		},

		{
			title: format('wifi_batch_change', { default: 'Wi-Fi batch change' }),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-wifi`,
			id: 2,
			callback: () => {
				onUpdateClick('change');
				setShowDropdownMenu(false);
			},
		},

		{
			title: format('sub_settings_title_appliance_type', {
				default: 'Appliance Type',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-outlet`,
			id: 3,
			callback: () => {
				onChangeClick('appliance_type');
				setShowDropdownMenu(false);
			},
		},

		{
			title: capFirstChar(
				format('sub_settings_title_exclude_event_log', {
					default: 'Exclude from activity log',
				}),
			),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-file-xmark`,
			id: 4,
			callback: () => {
				onChangeClick('exclude_event_log');
				setShowDropdownMenu(false);
			},
		},

		{
			title: format('sub_settings_title_consumption_calculation', {
				default: 'Consumption Calculation',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-bolt`,
			id: 5,
			callback: () => {
				onChangeClick('consumption_calculation');
				setShowDropdownMenu(false);
			},
		},

		userData?.premium === 1 && {
			title: format('forgotten_lights_reminder', {
				default: 'Forgotten lights reminder',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-lightbulb-exclamation-on`,
			id: 6,
			callback: () => {
				onChangeClick('exclude_forgotten_lights');
				setShowDropdownMenu(false);
			},
		},

		{
			title: format('sub_settings_title_offline_notifications', {
				default: 'Offline notifications',
			}),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-wifi-slash`,
			id: 7,
			callback: () => {
				onChangeClick('offline_notifications');
				setShowDropdownMenu(false);
			},
		},

		{
			title: capFirstChar(
				format('sub_settings_title_ai_model', {
					default: 'AI Model',
				}),
			),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-sparkles`,
			id: 8,
			showDropdown: false,
			rightIcon: 'fa-solid fa-chevron-right',
			items: [
				{
					title: format('text_enable_ai_model'),
					aiSubtype: 'enable_ai_model' as UpdateModeSubTypes,
					id: 9,
					icon: 'fa-solid fa-toggle-on text-success',
				},
				{
					title: format('text_disable_ai_model'),
					aiSubtype: 'disable_ai_model' as UpdateModeSubTypes,
					id: 10,
					icon: 'fa-solid fa-toggle-off text-danger',
				},
				{
					title: format('manage_ai_notifications'),
					aiSubtype: 'manage_ai_notifications' as UpdateModeSubTypes,
					id: 11,
					icon: 'fa-solid fa-bell',
				},
			],
		},

		isTestUser && {
			title: capFirstChar(
				format('sub_settings_title_measure_peak_power', {
					default: 'Measure Peak Power',
				}),
			),
			icon: `fa-solid fa-gauge-max`,
			id: 9,
			callback: () => {
				onChangeClick('measure_peak_power');
				setShowDropdownMenu(false);
			},
		},

		{
			title: capFirstChar(format('sub_settings_title_pin_lock', { default: 'Pin lock' })),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-key`,
			id: 10,
			callback: () => {
				onChangeClick('pin_lock');
				setShowDropdownMenu(false);
			},
		},

		isCloneDeviceSettingsEnabled() && {
			title: capFirstChar(
				format('sub_settings_title_apply_settings_configuration', {
					default: 'Apply settings configuration',
				}),
			),
			icon: `${newLayout ? 'fa-solid' : 'fa-light'} fa-cog`,
			id: 11,
			callback: () => {
				onChangeClick('apply_settings_configuration');
				setShowDropdownMenu(false);
				deps.dispatchSubmit?.('submit-click');
			},
		},
	].filter(Boolean) as MenuCommand[];

	return {
		initialEditCommands,
		bulkCommands,
	};
}

export type DropdownItem = {
	id: number;
	title: string;
	titleDefault: string;
	icon: string;
	disabled?: boolean;
	isSelectable?: boolean;
	titleClasses?: string;
	subtitle?: string;
	avatar?: any;
	onClick?: () => void;
	showDropdown?: boolean;
	rightIcon?: string;
	active?: boolean;
	items?: Array<{
		title: string;
		aiSubtype?: UpdateModeSubTypes;
		id: number;
		icon: string;
		[key: string]: any;
	}>;
	[key: string]: any; // Allow additional properties
};

export function menuCommandsToDropdownItems(
	commands: MenuCommand[],
	opts: {
		idOffset: number;
	},
	disableBulkActions?: boolean,
): DropdownItem[] {
	const { idOffset } = opts;

	return commands.map((cmd, idx) => {
		const resolved = cmd.title;

		const baseItem: DropdownItem = {
			id: idOffset + idx,
			title: resolved,
			titleDefault: resolved,
			icon: cmd.icon,
			disabled: disableBulkActions,
			isSelectable: cmd.isSelected ?? true,
			onClick: cmd.callback,
		};

		const additionalProps: Partial<DropdownItem> = {};

		if (cmd.showDropdown !== undefined) {
			additionalProps.showDropdown = cmd.showDropdown;
		}

		if (cmd.rightIcon) {
			additionalProps.rightIcon = cmd.rightIcon;
		}

		if (cmd.items) {
			additionalProps.items = cmd.items;
		}

		// Copy any other properties that aren't already handled
		const handledKeys = new Set([
			'title',
			'icon',
			'id',
			'isSelected',
			'callback',
			'areFiltersDisabled',
			'showDropdown',
			'rightIcon',
			'items',
		]);

		Object.keys(cmd).forEach((key) => {
			if (!handledKeys.has(key) && cmd[key] !== undefined) {
				additionalProps[key] = cmd[key];
			}
		});

		return {
			...baseItem,
			...additionalProps,
		};
	});
}
